import { useState, useEffect } from 'react';
import { FileText, Plus, Trash2, Printer, Building2, User, Eye } from 'lucide-react';
import { toast } from './Toast.jsx';
import { marcaDeLaAgencia } from '../marcaDeLaAgencia.js';
import {
  money, documentoCompleto, cuerpoDelPaystub, cuerpoDel1099,
} from '../documentosDeNomina.js';

// ─────────────────────────────────────────────────────────────────────────────
// Payroll & Tax Forms — MVP (Phase 1)
//
// Generates printable / save-as-PDF documents for the agency:
//   • Paystub   — periodic pay receipt (gross, deductions, net, YTD)
//   • 1099-NEC  — year-end non-employee compensation (recipient copy)
//
// Scope per Lissette (2026-06-09): MANUAL entry, PDF only. NO tax calculation,
// NO IRS e-file, NO database. Everything is in-memory; the only thing kept is
// the agency's own payer info in localStorage so it doesn't have to be retyped.
// SSN/TIN of the recipient is used only to render the PDF and is NOT stored.
//
// The PDF uses the app's established pattern: build a clean HTML document and
// open it in a new window for the browser's "Save as PDF" / print. (No pdf-lib
// here, so accents/emoji in the browser render fine.)
// ─────────────────────────────────────────────────────────────────────────────

const PAYER_KEY = 'deskte_payer_info_v1';

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const currentYear = () => new Date().getFullYear();


const loadPayer = () => {
  try { return JSON.parse(localStorage.getItem(PAYER_KEY) || '{}') || {}; } catch { return {}; }
};

// Open a print window with the given <title> and inner <body> HTML.
/**
 * Abre el documento en una ventana nueva.
 *
 * `imprimir` decide si ademas se lanza el dialogo de impresion. En FALSE es
 * solo verlo — Lissette, 2026-09-03, queja 61: *"me gustaria tener la opcion de
 * ver ese PDF sin imprimir"*. Hasta hoy la unica forma de mirar un recibo era
 * pasar por el dialogo de impresion y cancelarlo.
 *
 * La impresion espera a que carguen las IMAGENES. El logo de la agencia es un
 * archivo remoto: con el `setTimeout` fijo de antes, un logo que tardara mas de
 * medio segundo salia impreso como un hueco. Con `w.onload` se imprime cuando
 * la ventana esta entera, y el temporizador se queda solo de red de seguridad
 * por si una imagen no llega nunca.
 */
function printDoc(title, bodyHtml, imprimir = true) {
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) { toast.error('No se pudo abrir la ventana de impresión (revisa el bloqueador de pop-ups)'); return; }
  try { w.opener = null; } catch {}
  w.document.write(documentoCompleto(title, bodyHtml));
  w.document.close();
  if (!imprimir) return;
  let yaFue = false;
  const lanzar = () => { if (yaFue) return; yaFue = true; try { w.print(); } catch { /* la ventana pudo cerrarse */ } };
  try { w.onload = lanzar; } catch { /* noop */ }
  setTimeout(lanzar, 2500);
}

export default function PayrollDocsView({ lang, currentUser, expenses = [] }) {
  // La marca de la agencia para los documentos (queja 62).
  //
  // Lissette, 2026-09-03: *"el PDF sale sin el logo del perfil del agente"*.
  // Antes esto leia solo `currentUser.agency.logoUrl` —el logo de Mi Agencia →
  // Marca— y el suyo esta cargado en MI PERFIL. `marcaDeLaAgencia` mira los
  // dos sitios, en el mismo orden que el backend, asi que el papel sale con el
  // logo que este cargado donde sea.
  const marca = marcaDeLaAgencia(currentUser);
  const es = lang === 'es';
  const [tab, setTab] = useState('paystub');
  // Payer info is server-backed (AgencySetting.payrollPayer) so it follows the
  // owner across devices. localStorage acts as an instant-load cache and the
  // migration source for data saved before the server move (2026-06-10).
  const [payer, setPayer] = useState(loadPayer);
  const [editPayer, setEditPayer] = useState(!loadPayer().name);
  const [payerSaving, setPayerSaving] = useState(false);
  useEffect(() => {
    let alive = true;
    import('../api.js')
      .then(({ getAgencySettings }) => getAgencySettings())
      .then((s) => {
        if (!alive) return;
        const server = s?.payrollPayer;
        if (server && server.name) { setPayer(server); setEditPayer(false); try { localStorage.setItem(PAYER_KEY, JSON.stringify(server)); } catch {} }
        // Server empty but local has data → keep showing local; it migrates up
        // on the next Save.
      })
      .catch(() => { /* offline / error — localStorage cache already shown */ });
    return () => { alive = false; };
  }, []);

  const savePayer = async () => {
    setPayerSaving(true);
    try { localStorage.setItem(PAYER_KEY, JSON.stringify(payer)); } catch {}
    try {
      const { updateAgencySettings } = await import('../api.js');
      await updateAgencySettings({ payrollPayer: payer });
      setEditPayer(false);
      toast.success(es ? 'Datos del pagador guardados (servidor)' : 'Payer info saved (server)');
    } catch (e) {
      // Saved locally at least — tell the user the server copy didn't stick.
      setEditPayer(false);
      toast.error((es ? 'Guardado local, pero no en el servidor: ' : 'Saved locally, but not on the server: ') + (e?.message || ''));
    } finally { setPayerSaving(false); }
  };

  // ── History (simple log of generated docs — NO TIN, server-backed) ──────────
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const loadHistory = async () => {
    setHistLoading(true);
    try { const { listPayrollDocs } = await import('../api.js'); setHistory(await listPayrollDocs()); }
    catch { /* offline — leave empty */ } finally { setHistLoading(false); }
  };
  useEffect(() => { loadHistory(); }, []);
  // `datos` = los campos con los que se armo el documento, para poder volver a
  // verlo desde el historial (Lissette 2026-09-03, queja 61). El TIN completo
  // NO viaja: se quita aqui Y el servidor lo vuelve a quitar, porque la unica
  // llave de esa puerta no puede depender de que el navegador se acuerde.
  const logHistory = async (type, recipientName, amount, period, datos) => {
    try {
      const { createPayrollDoc } = await import('../api.js');
      const row = await createPayrollDoc({
        type, recipientName,
        amount: (amount === '' || amount == null) ? null : amount,
        period: period || null,
        datos: datos || null,
      });
      setHistory(h => [row, ...h]);
    } catch { /* non-fatal: the PDF was still generated */ }
  };

  // Volver a ver un documento del historial. Se arma otra vez con SUS datos —
  // no se guardo ningun PDF— y se abre sin lanzar la impresion.
  const verDelHistorial = (h) => {
    if (!h || !h.datos) return;
    const titulo = h.type === '1099'
      ? `1099-NEC ${h.period || ''} — ${h.recipientName}`
      : (es ? `Paystub — ${h.recipientName}` : `Pay Stub — ${h.recipientName}`);
    printDoc(titulo, h.type === '1099' ? armar1099(h.datos) : armarPaystub(h.datos), false);
  };
  const removeHistory = async (id) => {
    const prev = history;
    setHistory(h => h.filter(x => x.id !== id));
    try { const { deletePayrollDoc } = await import('../api.js'); await deletePayrollDoc(id); }
    catch { setHistory(prev); toast.error(es ? 'No se pudo borrar' : 'Could not delete'); }
  };
  const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; } };

  // ── Paystub state ──────────────────────────────────────────────────────────
  const [ps, setPs] = useState({
    name: '', address: '', cityStateZip: '', ssnLast4: '',
    periodStart: '', periodEnd: '', payDate: todayLocal(),
    gross: '', ytdGross: '',
    deductions: [],
    notes: '',
  });
  const psNet = (() => {
    const g = Number(ps.gross) || 0;
    const d = ps.deductions.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    return g - d;
  })();
  const psTotalDed = ps.deductions.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  // ── 1099-NEC state ───────────────────────────────────────────────────────
  const [f1099, setF1099] = useState({
    year: String(currentYear() - 1),
    name: '', address: '', cityStateZip: '', tin: '',
    box1: '', box4: '', accountNumber: '',
    stateName: '', stateId: '', stateIncome: '', stateTax: '',
  });

  // Phase 2 — auto-fill Box 1 from the agency's recorded expenses. Sums every
  // expense in the "contractor" category (payee = the expense's `vendor`) for
  // the selected 1099 year. Only the amount + name come from here; the TIN
  // stays manual on purpose (sensitive — never auto-stored).
  const contractorByPayee = (() => {
    const yr = f1099.year;
    const map = {};
    for (const e of (expenses || [])) {
      if (e.category !== 'contractor') continue;
      if (!e.date || String(e.date).slice(0, 4) !== yr) continue;
      const payee = (e.vendor || '').trim();
      if (!payee) continue;
      map[payee] = (map[payee] || 0) + (Number(e.amount) || 0);
    }
    return map;
  })();
  const contractorPayees = Object.keys(contractorByPayee).sort((a, b) => a.localeCompare(b));
  const fillFromExpenses = (payee) => {
    if (!payee) return;
    const total = contractorByPayee[payee] || 0;
    setF1099(p => ({ ...p, name: p.name || payee, box1: total.toFixed(2) }));
    toast.success(es ? `Total ${money(total)} de "${payee}" (${f1099.year})` : `Total ${money(total)} for "${payee}" (${f1099.year})`);
  };

  // El cuerpo del documento se arma aparte de generarlo, porque ahora hay DOS
  // caminos que lo necesitan: "Ver" (solo mirarlo) y "Generar" (mirarlo e
  // imprimirlo). Dos copias del mismo HTML se separarian en cuanto alguien
  // tocara una.
  const armarPaystub = (guardados) => {
    // Con `guardados` se arma el recibo de UNA fila del historial; sin ellos,
    // el que se esta escribiendo ahora. Es el mismo HTML en los dos casos: dos
    // copias se separarian en cuanto alguien tocara una.
    return cuerpoDelPaystub({
      es, marca, payer,
      d: guardados || ps,
      emisor: currentUser?.name || currentUser?.email || '',
    });
  };

  const paystubListo = () => {
    if (!ps.name.trim()) { toast.error(es ? 'Falta el nombre del trabajador' : 'Missing worker name'); return false; }
    if (!payer.name) { toast.error(es ? 'Primero completa los datos del pagador (arriba)' : 'Fill in the payer info first (top)'); return false; }
    return true;
  };

  // VER: abre el documento y ya. Lissette, 2026-09-03, queja 61: hasta hoy la
  // unica forma de mirar un recibo era lanzar el dialogo de impresion y
  // cancelarlo. No se apunta en el historial: mirar no es emitir.
  const verPaystub = () => {
    if (!paystubListo()) return;
    printDoc(es ? `Paystub — ${ps.name}` : `Pay Stub — ${ps.name}`, armarPaystub(), false);
  };

  const generatePaystub = () => {
    if (!paystubListo()) return;
    printDoc(es ? `Paystub — ${ps.name}` : `Pay Stub — ${ps.name}`, armarPaystub());
    logHistory('paystub', ps.name, psNet, ps.payDate, ps);
  };

  const armar1099 = (guardados) => {
    // Igual que el paystub: el mismo documento, con los datos de ahora o con
    // los de una fila del historial. `delHistorial` es lo unico que cambia,
    // porque un 1099 traido del historial NO trae el TIN.
    return cuerpoDel1099({
      es, marca, payer,
      d: guardados || f1099,
      emisor: currentUser?.name || currentUser?.email || '',
      delHistorial: !!guardados,
      hoy: todayLocal(),
    });
  };

  const f1099Listo = () => {
    if (!f1099.name.trim()) { toast.error(es ? 'Falta el nombre del destinatario' : 'Missing recipient name'); return false; }
    if (!payer.name || !payer.ein) { toast.error(es ? 'Completa los datos del pagador incl. EIN (arriba)' : 'Fill in payer info incl. EIN (top)'); return false; }
    return true;
  };

  const ver1099 = () => {
    if (!f1099Listo()) return;
    printDoc(`1099-NEC ${f1099.year} — ${f1099.name}`, armar1099(), false);
  };

  const generate1099 = () => {
    if (!f1099Listo()) return;
    printDoc(`1099-NEC ${f1099.year} — ${f1099.name}`, armar1099());
    logHistory('1099', f1099.name, f1099.box1, f1099.year, { ...f1099, tin: '' });
  };

  // ── Styles ───────────────────────────────────────────────────────────────
  const card = { background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1.1rem', marginBottom: '.75rem' };
  const lbl = { display: 'block', fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-text3,#45688a)', fontWeight: 700, marginBottom: 3 };
  const inp = { width: '100%', padding: '.5rem .6rem', borderRadius: 8, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface,#0d1c2e)', color: 'var(--c-white,#eef6ff)', fontSize: '.82rem', fontFamily: 'inherit' };
  const field = (label, value, onChange, opts = {}) => (
    <div style={{ flex: opts.flex || 1, minWidth: opts.minWidth || 140 }}>
      <label style={lbl}>{label}</label>
      <input style={inp} type={opts.type || 'text'} value={value} placeholder={opts.ph || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
  const row = { display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' };
  const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.6rem 1rem', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#0099cc,#0055aa)', color: '#fff', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'inherit' };
  const secondaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '.6rem 1rem', borderRadius: 9, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'inherit' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '1rem' }}>
        <FileText size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)', margin: 0 }}>{es ? 'Nómina y Formularios' : 'Payroll & Tax Forms'}</h2>
      </div>

      {/* Payer info */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editPayer ? '.7rem' : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.9rem' }}>
            <Building2 size={16} style={{ color: 'var(--c-cyan,#00d4ff)' }} /> {es ? 'Datos del pagador (tu agencia)' : 'Payer info (your agency)'}
          </div>
          {!editPayer && <button onClick={() => setEditPayer(true)} style={{ background: 'none', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 7, padding: '.3rem .7rem', fontSize: '.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>{es ? 'Editar' : 'Edit'}</button>}
        </div>
        {editPayer ? (
          <>
            <div style={row}>
              {field(es ? 'Nombre legal' : 'Legal name', payer.name || '', v => setPayer(p => ({ ...p, name: v })), { ph: 'Dalcerinsurance LLC' })}
              {field('EIN', payer.ein || '', v => setPayer(p => ({ ...p, ein: v })), { ph: '12-3456789', minWidth: 120 })}
            </div>
            <div style={row}>
              {field(es ? 'Dirección' : 'Address', payer.address || '', v => setPayer(p => ({ ...p, address: v })))}
              {field(es ? 'Ciudad, Estado, ZIP' : 'City, State, ZIP', payer.cityStateZip || '', v => setPayer(p => ({ ...p, cityStateZip: v })))}
              {field(es ? 'Teléfono' : 'Phone', payer.phone || '', v => setPayer(p => ({ ...p, phone: v })), { minWidth: 130 })}
            </div>
            <button onClick={savePayer} disabled={payerSaving} style={{ ...primaryBtn, cursor: payerSaving ? 'wait' : 'pointer' }}>{payerSaving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}</button>
          </>
        ) : (
          <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5, marginTop: '.5rem' }}>
            <strong style={{ color: 'var(--c-white,#eef6ff)' }}>{payer.name || '—'}</strong>
            {payer.ein ? ` · EIN ${payer.ein}` : ''}<br />
            {[payer.address, payer.cityStateZip, payer.phone].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '.85rem' }}>
        {[['paystub', es ? 'Paystub' : 'Pay Stub'], ['1099', '1099-NEC'], ['history', (es ? 'Historial' : 'History') + (history.length ? ` (${history.length})` : '')]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: '.45rem .9rem', borderRadius: 8, border: `1px solid ${tab === id ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, background: tab === id ? 'var(--c-cyan-dim,rgba(0,212,255,.12))' : 'transparent', color: tab === id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', fontSize: '.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'paystub' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.88rem', marginBottom: '.7rem' }}>
            <User size={15} style={{ color: 'var(--c-cyan,#00d4ff)' }} /> {es ? 'Recibo de pago' : 'Pay stub'}
          </div>
          <div style={row}>
            {field(es ? 'Nombre del trabajador' : 'Worker name', ps.name, v => setPs(p => ({ ...p, name: v })), { flex: 2, minWidth: 200 })}
            {field(es ? 'Últimos 4 del SSN (opcional)' : 'SSN last 4 (optional)', ps.ssnLast4, v => setPs(p => ({ ...p, ssnLast4: v.replace(/\D/g, '').slice(0, 4) })), { minWidth: 130 })}
          </div>
          <div style={row}>
            {field(es ? 'Dirección' : 'Address', ps.address, v => setPs(p => ({ ...p, address: v })))}
            {field(es ? 'Ciudad, Estado, ZIP' : 'City, State, ZIP', ps.cityStateZip, v => setPs(p => ({ ...p, cityStateZip: v })))}
          </div>
          <div style={row}>
            {field(es ? 'Inicio del período' : 'Period start', ps.periodStart, v => setPs(p => ({ ...p, periodStart: v })), { type: 'date', minWidth: 150 })}
            {field(es ? 'Fin del período' : 'Period end', ps.periodEnd, v => setPs(p => ({ ...p, periodEnd: v })), { type: 'date', minWidth: 150 })}
            {field(es ? 'Fecha de pago' : 'Pay date', ps.payDate, v => setPs(p => ({ ...p, payDate: v })), { type: 'date', minWidth: 150 })}
          </div>
          <div style={row}>
            {field(es ? 'Pago bruto ($)' : 'Gross pay ($)', ps.gross, v => setPs(p => ({ ...p, gross: v })), { type: 'number', minWidth: 150 })}
            {field(es ? 'Bruto acumulado YTD ($, opcional)' : 'YTD gross ($, optional)', ps.ytdGross, v => setPs(p => ({ ...p, ytdGross: v })), { type: 'number', minWidth: 180 })}
          </div>

          <label style={{ ...lbl, marginTop: '.4rem' }}>{es ? 'Deducciones' : 'Deductions'}</label>
          {ps.deductions.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem', alignItems: 'center' }}>
              <input style={{ ...inp, flex: 2 }} placeholder={es ? 'Concepto (ej. impuestos, seguro)' : 'Description (e.g. tax, insurance)'} value={d.label} onChange={e => setPs(p => ({ ...p, deductions: p.deductions.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} />
              <input style={{ ...inp, flex: 1 }} type="number" placeholder="0.00" value={d.amount} onChange={e => setPs(p => ({ ...p, deductions: p.deductions.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) }))} />
              <button onClick={() => setPs(p => ({ ...p, deductions: p.deductions.filter((_, j) => j !== i) }))} style={{ background: 'none', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444', borderRadius: 7, padding: '.45rem', cursor: 'pointer', display: 'flex' }}><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setPs(p => ({ ...p, deductions: [...p.deductions, { label: '', amount: '' }] }))}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px dashed var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, padding: '.4rem .8rem', fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '.7rem' }}>
            <Plus size={14} /> {es ? 'Agregar deducción' : 'Add deduction'}
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,255,136,.08)', border: '1px solid rgba(0,255,136,.25)', borderRadius: 9, padding: '.6rem .9rem', marginBottom: '.8rem' }}>
            <span style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.9rem' }}>{es ? 'Pago neto' : 'Net pay'}</span>
            <span style={{ fontWeight: 800, color: 'var(--c-green,#00ff88)', fontSize: '1.05rem' }}>{money(psNet)}</span>
          </div>

          <div style={{ marginBottom: '.7rem' }}>
            <label style={lbl}>{es ? 'Notas (opcional)' : 'Notes (optional)'}</label>
            <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={ps.notes} onChange={e => setPs(p => ({ ...p, notes: e.target.value }))} />
          </div>

          {/* VER antes de imprimir (Lissette 2026-09-03, queja 61). Hasta hoy
              la unica forma de mirar el recibo era lanzar el dialogo de
              impresion y cancelarlo. */}
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            <button onClick={verPaystub} style={secondaryBtn}><Eye size={15} /> {es ? 'Ver' : 'Preview'}</button>
            <button onClick={generatePaystub} style={{ ...primaryBtn, flex: 1 }}><Printer size={15} /> {es ? 'Generar PDF' : 'Generate PDF'}</button>
          </div>
        </div>
      )}

      {tab === '1099' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.88rem', marginBottom: '.7rem' }}>
            <FileText size={15} style={{ color: 'var(--c-cyan,#00d4ff)' }} /> {es ? 'Formulario 1099-NEC' : 'Form 1099-NEC'}
          </div>

          {/* Auto-fill Box 1 from recorded contractor expenses */}
          <div style={{ background: 'rgba(52,211,153,.08)', border: '1px solid rgba(52,211,153,.25)', borderRadius: 9, padding: '.6rem .8rem', marginBottom: '.7rem' }}>
            <label style={{ ...lbl, color: '#34d399' }}>🤝 {es ? 'Jalar total desde Gastos (categoría "Pago a agente/contratista")' : 'Pull total from Expenses ("Agent/Contractor Payment" category)'}</label>
            {contractorPayees.length > 0 ? (
              <select style={{ ...inp, cursor: 'pointer' }} value="" onChange={e => fillFromExpenses(e.target.value)}>
                <option value="">{es ? `Elegir agente del año ${f1099.year}…` : `Choose an agent for ${f1099.year}…`}</option>
                {contractorPayees.map(p => <option key={p} value={p}>{p} — {money(contractorByPayee[p])}</option>)}
              </select>
            ) : (
              <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.5 }}>
                {es
                  ? `No hay gastos con categoría "Pago a agente/contratista" en ${f1099.year}. Regístralos en Gastos (Proveedor = nombre del agente) y aparecerán aquí para sumarlos solos.`
                  : `No "Agent/Contractor Payment" expenses found for ${f1099.year}. Record them in Expenses (Vendor = agent name) and they'll appear here to total automatically.`}
              </div>
            )}
          </div>

          <div style={row}>
            {field(es ? 'Año fiscal' : 'Tax year', f1099.year, v => setF1099(p => ({ ...p, year: v.replace(/\D/g, '').slice(0, 4) })), { minWidth: 110 })}
            {field(es ? 'Nombre del destinatario' : 'Recipient name', f1099.name, v => setF1099(p => ({ ...p, name: v })), { flex: 2, minWidth: 200 })}
            {field(es ? 'TIN del destinatario (SSN/EIN)' : 'Recipient TIN (SSN/EIN)', f1099.tin, v => setF1099(p => ({ ...p, tin: v })), { minWidth: 180 })}
          </div>
          <div style={row}>
            {field(es ? 'Dirección' : 'Address', f1099.address, v => setF1099(p => ({ ...p, address: v })))}
            {field(es ? 'Ciudad, Estado, ZIP' : 'City, State, ZIP', f1099.cityStateZip, v => setF1099(p => ({ ...p, cityStateZip: v })))}
          </div>
          <div style={row}>
            {field(es ? 'Casilla 1 — Compensación ($)' : 'Box 1 — Compensation ($)', f1099.box1, v => setF1099(p => ({ ...p, box1: v })), { type: 'number', minWidth: 200 })}
            {field(es ? 'Casilla 4 — Imp. federal retenido ($)' : 'Box 4 — Fed. tax withheld ($)', f1099.box4, v => setF1099(p => ({ ...p, box4: v })), { type: 'number', minWidth: 220 })}
            {field(es ? 'No. de cuenta (opcional)' : 'Account number (optional)', f1099.accountNumber, v => setF1099(p => ({ ...p, accountNumber: v })), { minWidth: 160 })}
          </div>
          <label style={{ ...lbl, marginTop: '.3rem' }}>{es ? 'Información estatal (opcional)' : 'State information (optional)'}</label>
          <div style={row}>
            {field(es ? 'Estado' : 'State', f1099.stateName, v => setF1099(p => ({ ...p, stateName: v })), { minWidth: 100 })}
            {field(es ? 'No. ID estatal' : 'State ID no.', f1099.stateId, v => setF1099(p => ({ ...p, stateId: v })), { minWidth: 120 })}
            {field(es ? 'Ingreso estatal ($)' : 'State income ($)', f1099.stateIncome, v => setF1099(p => ({ ...p, stateIncome: v })), { type: 'number', minWidth: 150 })}
            {field(es ? 'Imp. estatal retenido ($)' : 'State tax withheld ($)', f1099.stateTax, v => setF1099(p => ({ ...p, stateTax: v })), { type: 'number', minWidth: 170 })}
          </div>
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '.3rem' }}>
            <button onClick={ver1099} style={secondaryBtn}><Eye size={15} /> {es ? 'Ver' : 'Preview'}</button>
            <button onClick={generate1099} style={{ ...primaryBtn, flex: 1 }}><Printer size={15} /> {es ? 'Generar PDF (copia del destinatario)' : 'Generate PDF (recipient copy)'}</button>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.88rem', marginBottom: '.7rem' }}>
            <FileText size={15} style={{ color: 'var(--c-cyan,#00d4ff)' }} /> {es ? 'Historial de documentos emitidos' : 'Issued documents history'}
          </div>
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.7rem' }}>
            {es
              ? 'Registro de los Paystubs y 1099 que generaste. El PDF no se guarda: al pulsar 👁 el documento se vuelve a armar con sus datos y se abre sin imprimir. El TIN completo no se almacena.'
              : "A log of the paystubs and 1099s you generated. No PDF is stored: pressing 👁 rebuilds the document from its data and opens it without printing. The full TIN is never stored."}
          </div>
          {histLoading && history.length === 0 ? (
            <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.82rem', padding: '.5rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.6rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.82rem' }}>
              {es ? 'Aún no has generado documentos. Cuando generes un Paystub o 1099 aparecerán aquí.' : 'No documents yet. Generated paystubs / 1099s will show up here.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
              {history.map(h => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .75rem', borderRadius: 9, background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)' }}>
                  <span style={{ fontSize: '.58rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', padding: '2px 7px', borderRadius: 100, flexShrink: 0, background: h.type === '1099' ? 'rgba(52,211,153,.14)' : 'rgba(0,212,255,.12)', color: h.type === '1099' ? '#34d399' : 'var(--c-cyan,#00d4ff)' }}>{h.type === '1099' ? '1099' : 'Paystub'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.84rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.recipientName}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>{fmtDateTime(h.createdAt)}{h.period ? ` · ${h.period}` : ''}</div>
                  </div>
                  {h.amount != null && <span style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-green,#00ff88)', flexShrink: 0 }}>{money(h.amount)}</span>}
                  {/* VER sin imprimir (queja 61). Solo aparece cuando la fila
                      guarda con que se armo: lo emitido antes de este cambio no
                      se puede reconstruir, y un boton que no hace nada es peor
                      que no tenerlo. */}
                  {h.datos && (
                    <button onClick={() => verDelHistorial(h)} title={es ? 'Ver el documento (sin imprimir)' : 'View the document (no printing)'}
                      style={{ background: 'none', border: '1px solid rgba(0,212,255,.3)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 7, padding: '.3rem .45rem', cursor: 'pointer', display: 'flex', flexShrink: 0 }}><Eye size={13} /></button>
                  )}
                  <button onClick={() => removeHistory(h.id)} title={es ? 'Borrar del historial' : 'Delete from history'} style={{ background: 'none', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444', borderRadius: 7, padding: '.3rem .45rem', cursor: 'pointer', display: 'flex', flexShrink: 0 }}><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', lineHeight: 1.6, padding: '0 .2rem' }}>
        {es
          ? '⚠️ Documentos generados manualmente, solo PDF. No calculan impuestos ni se presentan ante el IRS. El 1099-NEC genera la copia del destinatario; la Copia A se presenta por separado (e-file). Verifica con tu contador.'
          : '⚠️ Manually generated, PDF only. No tax calculation or IRS filing. The 1099-NEC produces the recipient copy; Copy A is filed separately (e-file). Verify with your accountant.'}
      </div>
    </div>
  );
}
